import { io, type Socket } from 'socket.io-client';
import { ApiError } from './api';
import { SESSION_PROTOCOL_VERSION } from './session-core';
import { trackAnalytics } from './analytics';

export type GroupInteraction = 'same_brain' | 'read_me' | 'dont_match';
export type GroupStatus = 'lobby' | 'active' | 'completed' | 'cancelled' | 'expired';
export type GroupChoice = 'a' | 'b';
export type GroupReaction = '😂' | '😳' | '🔥' | '🤝';
export interface GroupGameState {
  totalRounds: number;
  currentRound: null | {
    id: string; number: number; phase: 'answering' | 'revealed'; role: 'chooser' | 'target' | 'predictor';
    spotlight: null | { userId: string; firstName: string }; question: string;
    options: Array<{ key: GroupChoice; label: string; imageUrl:string|null; imageAlt:string|null; fallbackImageUrl:string|null }>;
    myAnswer: null | { key: GroupChoice; label: string };
    lockedCount: number; requiredCount: number;
    reveal: null | {
      answers: Array<{
        userId: string; firstName: string; key: GroupChoice; label: string; alignedCount: number;
        role: 'choice' | 'actual' | 'prediction'; correctPrediction: boolean | null;
      }>;
      sides: Array<{ key: GroupChoice; label: string; count: number; names: string[] }>;
      sameBrain: null | { syncPercent: number };
      readMe: null | {
        actual: { userId: string; firstName: string; key: GroupChoice; label: string };
        correctPredictors: Array<{ userId: string; firstName: string }>;
      };
      dontMatch: null | { survived: boolean; boom: boolean };
      reactions: Array<{ reaction: GroupReaction; count: number }>;
      myReaction: GroupReaction | null;
    };
  };
  score: {
    completedRounds: number; syncPercent: number;
    correctPredictions: number; totalPredictions: number; accuracyPercent: number;
    survivedRounds: number; survivalPercent: number;
  };
}
export interface GroupRoomSnapshot {
  protocolVersion: 1;
  room: {
    id: string; status: GroupStatus; interactionType: GroupInteraction; revision: number;
    host: { firstName: string }; participantCount: number; readyCount: number;
    minParticipants: 3; maxParticipants: number; canStart: boolean;
    createdAt: string; updatedAt: string; expiresAt: string; startedAt: string | null; completedAt: string | null;
  };
  viewerRole: 'host' | 'participant' | 'unjoined';
  participants: Array<{
    userId: string; firstName: string; role: 'host' | 'participant'; ready: boolean; presence: 'present' | 'reconnecting';
  }>;
  game: GroupGameState | null;
}
export interface GroupShare { deepLink: string; fallbackShareUrl: string; preparedMessageId: string | null }
export interface CreatedGroupRoom { token: string; snapshot: GroupRoomSnapshot; share: GroupShare | null }

const tokenKey = 'utage.activeGroupToken';
export const rememberedGroupToken = (): string | null => localStorage.getItem(tokenKey);
export const rememberGroupToken = (token: string | null): void => {
  if (token) localStorage.setItem(tokenKey, token); else localStorage.removeItem(tokenKey);
};

const request = async <T>(path: string, initData: string, options?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    ...options, cache: 'no-store',
    headers: { Accept: 'application/json', Authorization: `tma ${initData}`, 'Content-Type': 'application/json', 'X-UTAGE-Protocol': String(SESSION_PROTOCOL_VERSION), ...options?.headers }
  });
  const body = await response.json().catch(() => ({})) as T & { error?: { code?: string; message?: string } };
  if (!response.ok) throw new ApiError(body.error?.message ?? 'Could not update this group.', body.error?.code);
  return body;
};

const action = (extra: Record<string, unknown> = {}): string => JSON.stringify({ actionId: crypto.randomUUID(), ...extra });
export const createGroupRoom = (initData: string, interactionType: GroupInteraction): Promise<CreatedGroupRoom> =>
  request('/group-rooms', initData, { method: 'POST', body: action({ interactionType }) });
export const loadGroupRoom = (token: string, initData: string): Promise<GroupRoomSnapshot> =>
  request(`/group-rooms/${encodeURIComponent(token)}`, initData);
export const joinGroupRoom = (token: string, initData: string): Promise<GroupRoomSnapshot> =>
  request(`/group-rooms/${encodeURIComponent(token)}/join`, initData, { method: 'POST', body: action() });
export const setGroupReady = (token: string, initData: string, ready: boolean): Promise<GroupRoomSnapshot> =>
  request(`/group-rooms/${encodeURIComponent(token)}/ready`, initData, { method: 'POST', body: action({ ready }) });
export const startGroupRoom = (token: string, initData: string): Promise<GroupRoomSnapshot> =>
  request(`/group-rooms/${encodeURIComponent(token)}/start`, initData, { method: 'POST', body: action() });
export const answerGroupRound = (token: string, initData: string, choice: GroupChoice): Promise<GroupRoomSnapshot> =>
  request(`/group-rooms/${encodeURIComponent(token)}/game/answer`, initData, { method: 'POST', body: action({ choice }) });
export const reactToGroupRound = (token: string, initData: string, reaction: GroupReaction): Promise<GroupRoomSnapshot> =>
  request(`/group-rooms/${encodeURIComponent(token)}/game/reaction`, initData, { method: 'POST', body: action({ reaction }) });
export const nextGroupRound = (token: string, initData: string): Promise<GroupRoomSnapshot> =>
  request(`/group-rooms/${encodeURIComponent(token)}/game/next`, initData, { method: 'POST', body: action() });
export const cancelGroupRoom = (token: string, initData: string): Promise<GroupRoomSnapshot> =>
  request(`/group-rooms/${encodeURIComponent(token)}/cancel`, initData, { method: 'POST', body: action() });
export const leaveGroupRoom = (token: string, initData: string): Promise<GroupRoomSnapshot> =>
  request(`/group-rooms/${encodeURIComponent(token)}/leave`, initData, { method: 'POST', body: action() });
export const refreshGroupShare = (token: string, initData: string): Promise<GroupShare> =>
  request(`/group-rooms/${encodeURIComponent(token)}/share`, initData, { method: 'POST', body: '{}' });

export const watchGroupRoom = (input: {
  token: string; initData: string; onSnapshot: (snapshot: GroupRoomSnapshot) => void; onConnection: (connected: boolean) => void;
}): (() => void) => {
  const socket: Socket = io({ auth: { initData: input.initData, protocolVersion: SESSION_PROTOCOL_VERSION }, reconnection: true });
  let disconnected = false;
  let disposed = false;
  socket.on('connect', () => {
    input.onConnection(true);
    if (disconnected) trackAnalytics('recovery_attempted', { scope: 'group', mechanism: 'socket_resume' });
    socket.emit('group:watch', { token: input.token }, (result: { ok: boolean; snapshot?: GroupRoomSnapshot }) => {
      if (result.ok && result.snapshot) {
        input.onSnapshot(result.snapshot);
        if (disconnected) trackAnalytics('recovery_succeeded', { scope: 'group', mechanism: 'socket_resume' });
        disconnected = false;
      }
    });
  });
  socket.on('disconnect', () => { if (!disposed) { disconnected = true; trackAnalytics('socket_disconnected', { scope: 'group' }); input.onConnection(false); } });
  socket.on('group:changed', () => void loadGroupRoom(input.token, input.initData).then(input.onSnapshot).catch(() => undefined));
  const restore = (): void => {
    if (document.visibilityState !== 'visible') return;
    trackAnalytics('recovery_attempted', { scope: 'group', mechanism: 'snapshot' });
    void loadGroupRoom(input.token, input.initData).then((snapshot) => {
      input.onSnapshot(snapshot);
      trackAnalytics('snapshot_recovery_used', { scope: 'group', mechanism: 'visibility' });
      trackAnalytics('recovery_succeeded', { scope: 'group', mechanism: 'snapshot' });
    }).catch(() => trackAnalytics('recovery_failed', { scope: 'group', mechanism: 'snapshot' }));
  };
  document.addEventListener('visibilitychange', restore);
  return () => { disposed = true; socket.disconnect(); document.removeEventListener('visibilitychange', restore); };
};
